import * as React from 'react'

/**
 * Single-class transitioning hook.
 *
 * Step 1: add a class style (`.hidden` works) with your usual hidden styles like opacity 0.
 * Step 2: use hook
 *
 * @param showing The prop controlling whether the element is currently showing.
 * @param appear If `true`, also animates the initial mount. Default is `false`.
 * @example
 *
 *   const [onTransitionEnd, rendering, hidden] = useCssTransition(props.showing)
 *   return (
 *     rendering && <div className={cx(styles.root, hidden && styles.hidden)} onTransitionEnd={onTransitionEnd} />
 *   )`
 */
export function useCssTransition(
  showing: boolean,
  appear = false
): [React.TransitionEventHandler, true | null, boolean] {
  const [rendering, setRendering] = React.useState(showing)
  // We tack hidden since we want to set it in the tick AFTER setting `rendering` to true.
  const [hidden, setHidden] = React.useState(appear)
  // When the transition ends, if we are not supposed to show the element, set `rendering` to false.
  const onTransitionEnd = React.useCallback(
    (e: React.TransitionEvent) => {
      if (e.target === e.currentTarget) {
        setRendering(showing)
      }
    },
    [showing]
  )
  // Used to respect the `appear` option.
  const isFirstRenderRef = React.useRef<boolean>(true)
  // When we want to show the element again, immediately set `rendering` to true.
  React.useEffect(() => {
    let frame: any = null
    if (showing) {
      // Render immediately
      setRendering(true)
      // Remove `hidden` immediately on first render if not using `appear`.
      if (isFirstRenderRef.current && !appear) {
        setHidden(false)
      } else {
        // On the next frame, remove the hidden attribute,
        frame = requestAnimationFrame(() => setHidden(false))
      }
    } else {
      // Immediately set hidden.
      // TransitionEnd event will set rendering to false.
      setHidden(true)
    }
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
    }
    return () => cancelAnimationFrame(frame)
  }, [showing, appear])
  return [onTransitionEnd, rendering || null, hidden]
}
